
import { Component, Prop, Vue } from 'vue-property-decorator';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VueTagsInput, createTags } from '@johmun/vue-tags-input';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faMap, faCrosshairs } from '@fortawesome/free-solid-svg-icons';

import BackToTop from 'vue-backtotop';

import * as L from 'leaflet';

import * as _ from 'lodash';

/* eslint-disable global-require */
import { BootstrapVue, IconsPlugin } from 'bootstrap-vue';

// app.js
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';

import { Icon } from 'leaflet';
import { LMap, LTileLayer, LMarker } from 'vue2-leaflet';
import ViewColumn from './ViewColumn.vue';
import PointModal from './PointModal.vue';
import '../../node_modules/leaflet/dist/leaflet.css';

import '../main.css';

Vue.use(BackToTop);

library.add(faMap);
library.add(faCrosshairs);

Vue.config.productionTip = false;

Vue.use(BootstrapVue);
Vue.use(IconsPlugin);

Vue.component('l-map', LMap);
Vue.component('l-tile-layer', LTileLayer);
Vue.component('l-marker', LMarker);

type D = Icon.Default & {
  _getIconUrl: string;
};

// eslint-disable-next-line no-underscore-dangle
delete (Icon.Default.prototype as D)._getIconUrl;

Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

type Tag = {
  text: string;
};

const LNG_LAT_OPTION = 'lng/lat';
const LAT_LNG_OPTION = 'lat/lng';

@Component({
  components: {
    PointModal,
    VueTagsInput,
    FontAwesomeIcon,
    ViewColumn,
  },
})
export default class CompareView extends Vue {
  @Prop() private isBuiltForApi!: boolean;

  @Prop() private isBuiltForSpa!: boolean;

  ids: string | null = '';

  text: string | null = '';

  private point: L.LatLng | null = null;

  private pointStr: string | null = '';

  private host = '';

  private hosts: Tag[] = [];

  private autocomplete = true;

  private geometries: string | null = 'source';

  private debug = '0';

  private queryPath = '';

  private extraParams = '';

  private structured: Record<string, string | undefined | null> = {
    venue: undefined,
    address: undefined,
    neighbourhood: undefined,
    borough: undefined,
    locality: undefined,
    county: undefined,
    region: undefined,
    postalcode: undefined,
    country: undefined,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private responses: any[] = [];

  private endpoint = '/v1/autocomplete';

  private isDoingGeolocation = false;

  pointOptions = [LNG_LAT_OPTION, LAT_LNG_OPTION];

  pointPlaceholderText = 'Longitude, Latitude';

  selectedPointOption = LNG_LAT_OPTION;

  pointModalShown() {
    (this.$refs.pointModal as PointModal).invalidateSize();
  }

  tagsChanged(newHosts: Tag[]) {
    this.hosts = newHosts;
    // ugh, for historical reasons, we save these in local storage as "endpoints"
    window.localStorage.setItem('endpoints', this.hosts.map((h) => h.text).join(','));
    this.onChange();
  }

  pointOptionsChanged(newOption: string) {
    if (newOption === LNG_LAT_OPTION) {
      this.pointPlaceholderText = 'Longitude, Latitude';
    } else {
      this.pointPlaceholderText = 'Latitude, Longitude';
    }
    this.selectedPointOption = newOption;

    if (this.pointStr) {
      this.onPointStringInputChange(this.pointStr);
    }
  }

  parseQuery(query: string) {
    const parts = query.split('?');
    // eslint-disable-next-line prefer-destructuring
    this.endpoint = parts[0];
    const params = new URLSearchParams(parts[1]);

    this.debug = params.get('debug') || '0';
    params.delete('debug');

    this.text = params.get('text');
    params.delete('text');

    this.ids = params.get('ids');
    params.delete('ids');

    const parsePoint = (_prefix?: string) => {
      const prefix = _prefix ? `${_prefix}.` : '';

      const lat = params.get(`${prefix}point.lat`);
      const lon = params.get(`${prefix}point.lon`);
      if (!lat || !lon) {
        return null;
      }

      params.delete(`${prefix}point.lat`);
      params.delete(`${prefix}point.lon`);

      const latlng = new L.LatLng(parseFloat(lat.trim()), parseFloat(lon.trim()));

      this.pointStr = `${latlng.lng.toFixed(6)}, ${latlng.lat.toFixed(6)}`;
      this.selectedPointOption = 'lng/lat';

      return latlng;
    };

    this.point = parsePoint() || parsePoint('focus');

    _.keys(this.structured).forEach((structuredKey) => {
      this.structured[structuredKey] = params.get(structuredKey);
      params.delete(structuredKey);
    });

    this.geometries = params.get('geometries') || '';
    params.delete('geometries');

    this.extraParams = params.toString();
  }

  getPathFromUrl(): string {
    // If we're running in SPA mode with routing like
    // http://localhost:8080/v1/search?text=San+Nicolas%2C+Peru&debug=0
    if (this.isBuiltForSpa && window.location.pathname.length > 1) {
      return `/v1/${window.location.href.split('/v1/')[1]}`;
    }
    // If we're running in non-SPA mode with routing like
    // http://blackmad.github.io/pelias-compare/index.html#/v1/search?text=....
    return window.location.hash.substr(1);
  }

  parseHash(_hash: string) {
    let hash = _hash;
    // As a hueristic, only decode as uri component if there's a uri
    // escaped question mark in there. Copy and pasting a path onto the
    // url hash string won't automatically do this
    if (hash.includes('%3F')) {
      hash = decodeURIComponent(hash);
    }
    if (hash.length > 0) {
      this.parseQuery(hash);
      this.onChange();
      if (
        (this.text && this.endpointUsesText)
        || (this.point && this.isPointRequired)
        || (this.ids && this.endpointUsesIds)
      ) {
        this.onSubmit();
      }
    }
  }

  created() {
    this.hosts = createTags(['https://api.jawg.io/places', 'http://127.0.0.1:3100']);

    this.parseHash(this.getPathFromUrl());

    window.addEventListener(
      'hashchange',
      () => {
        this.parseHash(this.getPathFromUrl());
      },
      false,
    );
  }

  getParams() {
    const params = new URLSearchParams(`?${this.extraParams}`);

    if (this.isStructured) {
      _.forEach(this.structured, (value, key) => {
        if (value) {
          params.set(key, (value as unknown) as string);
        }
      });
    }

    if (this.point && this.endpointUsesPoint) {
      if (this.endpointUsesFocus) {
        params.set('focus.point.lat', this.point.lat.toString());
        params.set('focus.point.lon', this.point.lng.toString());
      } else {
        params.set('point.lat', this.point.lat.toString());
        params.set('point.lon', this.point.lng.toString());
      }
    }

    if (this.isGeometriesSupported) {
      if (this.geometries === 'source' || this.geometries === 'point') {
        params.set('geometries', this.geometries);
      } else {
        params.delete('geometries');
      }
    }

    if (this.text && this.endpointUsesText) {
      params.set('text', this.text);
    }

    if (this.ids && this.endpointUsesIds) {
      params.set('ids', this.ids);
    }

    if (this.debug !== undefined && this.debug && this.debug !== '0') {
      params.set('debug', this.debug);
    }
    return params;
  }

  onGeometriesChange(v: string) {
    this.geometries = v;
    this.onChange();
  }

  // We need this because checkboxes don't seem to sync before calling change
  // unlike text fields
  onDebugChange(v: string) {
    this.debug = v;
    this.onChange();
  }

  // We need this because checkboxes don't seem to sync before calling change
  // unlike text fields
  onEndpointChange(v: string) {
    this.endpoint = v;
    this.onChange();
  }

  onPointStringInputChange(v: string) {
    const parseToParts = () => {
      if (v.includes('{') || v.includes('[')) {
        const match = v.match(/([+-]?\d+\.\d+)[^\d]+([+-]?\d+\.\d+)/);
        if (!match) {
          // eslint-disable-next-line no-alert
          window.alert(`Could not parse coordinates from ${v}`);
          throw new Error('Could not parse coordinates');
        }
        // assume this is coming from geojson so it's lng/lat
        this.selectedPointOption = LNG_LAT_OPTION;
        return [Number.parseFloat(match[1]), Number.parseFloat(match[2])];
      }
      const parts = v.split(',');

      const part1 = parseFloat(parts[0].trim());
      const part2 = parseFloat(parts[1].trim());
      return [part1, part2];
    };

    const [part1, part2] = parseToParts();

    if (this.selectedPointOption === LNG_LAT_OPTION) {
      this.point = new L.LatLng(part2, part1);
    } else {
      this.point = new L.LatLng(part1, part2);
    }

    this.onChange();
  }

  updatePoint(point: L.LatLng) {
    this.point = point;
    this.pointChanged();
  }

  pointChanged() {
    if (!this.point) {
      return;
    }
    if (this.selectedPointOption === LNG_LAT_OPTION) {
      this.pointStr = `${this.point.lng},${this.point.lat}`;
    } else {
      this.pointStr = `${this.point.lat},${this.point.lng}`;
    }
    this.onChange();
  }

  updateHashFromChild(queryPath: string) {
    this.parseHash(queryPath);

    this.onSubmit();
  }

  updateHash(queryPath: string) {
    if (this.isBuiltForSpa) {
      window.history.pushState({}, '', queryPath);
    } else {
      window.history.pushState({}, '', `#${queryPath}`);
    }

    document.title = `Pelias Compare Tool: ${this.text || this.ids || this.point || this.endpoint}`;

    this.$forceUpdate();
  }

  onChange() {
    const params = this.getParams();

    this.queryPath = `${this.endpoint}?${params.toString()}`;

    this.updateHash(this.queryPath);
  }

  get prettyExtraParams() {
    return decodeURIComponent(this.extraParams).replace(/\+/g, ' ');
  }

  set prettyExtraParams(humanReadableExtraParams: string) {
    this.extraParams = humanReadableExtraParams;
  }

  get prettyQueryPath() {
    return decodeURIComponent(this.queryPath).replace(/\+/g, ' ');
  }

  set prettyQueryPath(humanReadablePath: string) {
    this.parseQuery(humanReadablePath);
    this.onChange();
    this.queryPath = humanReadablePath;
  }

  getKey = (domain: string) => {
    if (domain.startsWith('https://api.jawg.io')) {
      return 'QJ4CPBBP165BvXSRHZ3G7y7Yb1tVWMK3kj7fM8VXQH5UM6tw8KPWVZsc1RnEUNYb';
    }
    return '';
  };

  onSubmit() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responsePromises: Promise<any>[] = this.hosts.map(async (_host: Tag) => {
      let host = _host.text;
      if (host.endsWith('/')) {
        host = host.substring(0, host.length - 1);
      }

      let { queryPath } = this;

      // eslint-disable-next-line no-prototype-builtins
      if (!queryPath.includes('api_key')) {
        const key = this.getKey(host);
        if (key) {
          // eslint-disable-next-line @typescript-eslint/camelcase
          queryPath += `&api_key=${key}`;
        }
      }

      const urlWithParams = `${host}${queryPath}`;

      // eslint-disable-next-line prefer-const
      let { status, data } = await fetch(urlWithParams, {
        method: 'GET',
      })
        .then(async (response) => ({ data: await response.json(), status: response.status }))
        .catch((err) => {
          window.console.warn(err);
          return {
            status: '',
            data: {
              error: err,
            },
          };
        });

      const errors = data?.geocoding?.errors || [];
      const warnings = data?.geocoding?.warnings || [];

      if (!data?.geocoding) {
        let message = `HTTP ${status} status from ${host}`;
        if (data && data.error) {
          message = data.error;
        }

        errors.push(message);
      }

      return {
        url: urlWithParams,
        status,
        host,
        body: data,
        errors,
        warnings,
        bodyString: `${JSON.stringify(data, null, 2)}\n\n`,
      };
    });
    Promise.all(responsePromises)
      .then((r) => {
        this.responses = r;
      })
      .catch((e) => {
        window.console.error(e);
      });
  }

  get endpointUsesText() {
    return ['/v1/search', '/v1/autocomplete'].includes(this.endpoint);
  }

  get endpointUsesIds() {
    return ['/v1/place'].includes(this.endpoint);
  }

  get isPointRequired() {
    return this.endpoint === '/v1/reverse';
  }

  get isStructured() {
    return this.endpoint === '/v1/search/structured';
  }

  get endpointUsesPoint() {
    return ['/v1/search', '/v1/autocomplete', '/v1/search/structured', '/v1/reverse'].includes(
      this.endpoint,
    );
  }

  get isGeometriesSupported() {
    return ['/v1/place'].includes(this.endpoint);
  }

  get endpointUsesFocus() {
    return ['/v1/search', '/v1/autocomplete', '/v1/search/structured'].includes(this.endpoint);
  }

  get pointLabel() {
    if (this.endpointUsesFocus) {
      return 'Focus';
    }
    return 'Point';
  }

  get pointLat() {
    return this.point?.lat;
  }

  get pointLng() {
    return this.point?.lng;
  }

  // eslint-disable-next-line class-methods-use-this
  get isGeolocationSupported() {
    return window.location.protocol === 'https:' && navigator.geolocation;
  }

  doGeolocate() {
    this.isDoingGeolocation = true;
    navigator.geolocation.getCurrentPosition(
      (pos: Position) => {
        this.isDoingGeolocation = false;
        const crd = pos.coords;
        this.point = new L.LatLng(crd.latitude, crd.longitude);
        this.pointChanged();
      },
      (error: any) => {
        this.isDoingGeolocation = false;
      },
    );
  }
}
